import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { unregisterBrowserPush } from '../services/browserPush';
import { applyTheme, isPublicRoute, resetThemeToDefaults, resetFaviconToDefault, updatePwaManifest, resetPwaManifest } from '../utils/themeUtils';
import { clearNavCache } from '../utils/navDataCache';
import { clearAcademicScope } from '../utils/branchUtils';

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Safe localStorage helpers — prevent corrupt JSON from crashing the app
function safeGetItem(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw || raw === 'undefined' || raw === 'null') return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function safeSetItem(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.warn(`Failed to save ${key} to localStorage`, e);
  }
}
// Client-side JWT expiry check
function isTokenExpired(token) {
  if (!token || typeof token !== 'string') return true;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const payload = JSON.parse(jsonPayload);
    if (payload.exp && payload.exp * 1000 <= Date.now()) {
      return true;
    }
    return false;
  } catch {
    return true;
  }
}

function syncInstituteBranding(userData) {
  if (!userData) return;
  if (
    userData.institute_name ||
    userData.institute_logo ||
    userData.institute_favicon ||
    userData.institute_primary_color ||
    userData.institute_secondary_color
  ) {
    const brandingData = {
      institute_id: userData.institute_id || userData.id || '',
      name: userData.institute_name || '',
      logo: userData.institute_logo || '',
      favicon: userData.institute_favicon || userData.institute_logo || '',
      primary_color: userData.institute_primary_color || '',
      secondary_color: userData.institute_secondary_color || '',
      banner: userData.institute_banner || '',
      tagline: userData.institute_tagline || '',
      city: userData.institute_city || '',
      state: userData.institute_state || '',
    };
    localStorage.setItem('institute_branding', JSON.stringify(brandingData));
    if ((brandingData.primary_color || brandingData.secondary_color) && !isPublicRoute()) {
      applyTheme(brandingData);
    }
    // Update PWA manifest and install icons for laptop and mobile according to login
    updatePwaManifest(brandingData);
    window.dispatchEvent(new CustomEvent('institute-branding-updated', { detail: brandingData }));
  }
}

export const AuthProvider = ({ children }) => {
  // userRef holds the current user synchronously (avoids React state async delay)
  const userRef = useRef(null);
  const [user, _setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const [isImpersonating, setIsImpersonating] = useState(() => {
    return typeof window !== 'undefined' && Boolean(sessionStorage.getItem('original_superadmin_token'));
  });

  // Always keep ref in sync with state
  const setUser = (u) => {
    userRef.current = u;
    _setUser(u);
  };

  const clearSession = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('institute_branding');
    clearAcademicScope();
    clearNavCache();
    sessionStorage.removeItem('original_superadmin_token');
    sessionStorage.removeItem('original_superadmin_user');
    setIsImpersonating(false);
    delete axios.defaults.headers.common['Authorization'];
    resetThemeToDefaults();
    resetFaviconToDefault();
    resetPwaManifest();
    if (typeof document !== 'undefined') {
      document.documentElement.classList.remove('dark');
      document.documentElement.style.colorScheme = 'light';
    }
    setUser(null);
  };


  // Setup Axios 401 Interceptor for expired/invalid token handling
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error.response?.status;
        const url = error.config?.url || '';

        // Ignore 401 on login, register, forgot-password, reset-password credential checks
        const isAuthCredentialEndpoint =
          url.includes('/auth/login') ||
          url.includes('/auth/register') ||
          url.includes('/auth/forgot-password') ||
          url.includes('/auth/reset-password') ||
          url.includes('/auth/verify-reset-token');

        // A super admin without two-step sign-in: the panel stays closed until it is on
        if (status === 403 && error.response?.data?.detail?.code === 'two_factor_setup_required') {
          if (window.location.pathname !== '/setup-two-step') {
            window.history.pushState({}, '', '/setup-two-step');
            window.dispatchEvent(new PopStateEvent('popstate'));
          }
          return Promise.reject(error);
        }

        // The institute's plan ended and its grace period is over: show the "plan expired" screen
        if (status === 402 && error.response?.data?.detail?.code === 'plan_expired') {
          if (window.location.pathname !== '/plan-expired') {
            window.history.pushState({}, '', '/plan-expired');
            window.dispatchEvent(new PopStateEvent('popstate'));
          }
          return Promise.reject(error);
        }

        if (status === 402 && ['billing_overdue', 'billing_blocked'].includes(error.response?.data?.detail?.code)) {
          window.dispatchEvent(new CustomEvent('coachhub-billing-restriction', {
            detail: error.response.data.detail,
          }));
          return Promise.reject(error);
        }

        if (status === 401 && !isAuthCredentialEndpoint) {
          const hadToken = !!localStorage.getItem('token');
          clearSession();

          const publicPaths = ['/login', '/register', '/reset-password', '/', '/features', '/institutes', '/about', '/become-a-partner'];
          const currentPath = window.location.pathname;
          const isPublic = publicPaths.includes(currentPath) || 
            currentPath.startsWith('/institute/') || 
            currentPath.startsWith('/institutes/') || 
            currentPath.startsWith('/page/') || 
            currentPath.startsWith('/report/') || 
            currentPath.startsWith('/pay/');

          if (hadToken && !isPublic) {
            toast.error('Session expired. Please log in again.');
            setTimeout(() => {
              if (window.location.pathname !== '/login') {
                window.location.href = '/login';
              }
            }, 300);
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const restoreSession = async () => {
      try {
        const token = localStorage.getItem('token');
        const parsedUser = safeGetItem('user');

        if (token && parsedUser && typeof parsedUser === 'object') {
          // Check local token expiry first
          if (isTokenExpired(token)) {
            console.info('Saved token is expired, clearing session.');
            clearSession();
            return;
          }

          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          try {
            const requestConfig = { timeout: 10000 };
            const res = await axios.get(`${API}/auth/me`, requestConfig);
            let hydratedUser = res.data;
            if (hydratedUser?.custom_role_id) {
              try {
                const roleRes = await axios.get(`${API}/roles/${hydratedUser.custom_role_id}`, requestConfig);
                hydratedUser = { ...hydratedUser, custom_role: roleRes.data };
              } catch {}
            }
            if (!cancelled && hydratedUser) {
              if (hydratedUser.institute_category) {
                localStorage.setItem('institute_category', hydratedUser.institute_category);
              }
              if (hydratedUser.institute_subcategory) {
                localStorage.setItem('institute_subcategory', hydratedUser.institute_subcategory);
              }
              safeSetItem('user', hydratedUser);
              syncInstituteBranding(hydratedUser);
              setUser(hydratedUser);
              return;
            }
          } catch (apiErr) {
            // If backend says 401/403, token is invalid/expired -> clear immediately
            if (apiErr.response?.status === 401 || apiErr.response?.status === 403) {
              console.warn('Session verification failed with 401/403, clearing auth.');
              clearSession();
              return;
            }
            // For temporary network errors / offline, allow cached session
            if (!cancelled) {
              syncInstituteBranding(parsedUser);
              setUser(parsedUser);
            }
            return;
          }
        } else {
          // No valid session or corrupt data — clear everything
          clearSession();
        }
      } catch (error) {
        console.warn('Failed to restore auth session, clearing stale local auth state.', error);
        clearSession();
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    restoreSession();

    return () => {
      cancelled = true;
    };
  }, []);

  // Password first. With two-step sign-in on, the API answers with a challenge instead of a
  // session: the caller asks for the authenticator code and calls completeTwoFactor.
  const login = async (email, password, extra = {}) => {
    const res = await axios.post(`${API}/auth/login`, { email, password, ...extra });
    if (res.data?.two_factor_required) {
      return { twoFactorRequired: true, challenge: res.data.challenge };
    }
    return finishLogin(res.data);
  };

  const completeTwoFactor = async (challenge, code) => {
    const res = await axios.post(`${API}/auth/login/2fa`, { challenge, code });
    return finishLogin(res.data);
  };

  const finishLogin = async ({ access_token, user: userData }) => {
    if (userData?.institute_category) {
      localStorage.setItem('institute_category', userData.institute_category);
    }
    if (userData?.institute_subcategory) {
      localStorage.setItem('institute_subcategory', userData.institute_subcategory);
    }
    if (userData?.institute_category || userData?.institute_subcategory) {
      window.dispatchEvent(new Event('institute-category-changed'));
    }
    // Store in localStorage
    clearAcademicScope();
    localStorage.setItem('token', access_token);
    safeSetItem('user', userData);
    syncInstituteBranding(userData);
    // Set axios header immediately
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    const me = await axios.get(`${API}/auth/me`);
    let hydratedUser = me.data;
    if (hydratedUser?.custom_role_id) {
      try {
        const roleRes = await axios.get(`${API}/roles/${hydratedUser.custom_role_id}`);
        hydratedUser = { ...hydratedUser, custom_role: roleRes.data };
      } catch {}
    }
    if (hydratedUser?.institute_category) {
      localStorage.setItem('institute_category', hydratedUser.institute_category);
    }
    if (hydratedUser?.institute_subcategory) {
      localStorage.setItem('institute_subcategory', hydratedUser.institute_subcategory);
    }
    if (hydratedUser?.institute_category || hydratedUser?.institute_subcategory) {
      window.dispatchEvent(new Event('institute-category-changed'));
    }
    safeSetItem('user', hydratedUser);
    syncInstituteBranding(hydratedUser);
    // Update both ref and React state — ref is synchronous so navigation won't race
    setUser(hydratedUser);
    return hydratedUser;
  };

  // extras: optional growth attribution (referral_code, referred_via, utm_source, claim_listing_id).
  const register = async (name, email, password, role, phone, category = 'School', subcategory = '', extras = {}) => {
    const res = await axios.post(`${API}/auth/register`, { name, email, password, role, phone, category, subcategory, ...extras });
    const { access_token, user: userData } = res.data;
    const initialCategory = userData?.institute_category || category;
    const initialSubcategory = userData?.institute_subcategory || subcategory || '';
    if (initialCategory) {
      localStorage.setItem('institute_category', initialCategory);
    }
    if (initialSubcategory) {
      localStorage.setItem('institute_subcategory', initialSubcategory);
    }
    window.dispatchEvent(new Event('institute-category-changed'));
    clearAcademicScope();
    localStorage.setItem('token', access_token);
    safeSetItem('user', userData);
    syncInstituteBranding(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    const me = await axios.get(`${API}/auth/me`);
    let hydratedUser = me.data;
    if (hydratedUser?.custom_role_id) {
      try {
        const roleRes = await axios.get(`${API}/roles/${hydratedUser.custom_role_id}`);
        hydratedUser = { ...hydratedUser, custom_role: roleRes.data };
      } catch {}
    }
    const finalCategory = hydratedUser?.institute_category || initialCategory;
    const finalSubcategory = hydratedUser?.institute_subcategory || initialSubcategory || '';
    if (finalCategory) {
      localStorage.setItem('institute_category', finalCategory);
    }
    if (finalSubcategory) {
      localStorage.setItem('institute_subcategory', finalSubcategory);
    }
    window.dispatchEvent(new Event('institute-category-changed'));
    safeSetItem('user', hydratedUser);
    syncInstituteBranding(hydratedUser);
    setUser(hydratedUser);
    return hydratedUser;
  };

  const logout = async () => {
    try { await unregisterBrowserPush(); } catch (error) { console.warn('Push token cleanup failed', error); }
    clearSession();
    // Reset CSS brand variables and favicon to CoachHub defaults
    // so public pages aren't affected by institute branding
    resetThemeToDefaults();
    resetFaviconToDefault();
  };

  const updateUser = (updates) => {
    const updated = { ...user, ...updates };
    if (updates?.institute_category) {
      localStorage.setItem('institute_category', updates.institute_category);
      window.dispatchEvent(new Event('institute-category-changed'));
    }
    safeSetItem('user', updated);
    setUser(updated);
  };

  const impersonateInstitute = async (impersonateToken, userData) => {
    const currentToken = localStorage.getItem('token');
    const currentUser = safeGetItem('user');

    if (currentToken && currentUser) {
      sessionStorage.setItem('original_superadmin_token', currentToken);
      sessionStorage.setItem('original_superadmin_user', JSON.stringify(currentUser));
      setIsImpersonating(true);
    }

    if (userData?.institute_category) {
      localStorage.setItem('institute_category', userData.institute_category);
      window.dispatchEvent(new Event('institute-category-changed'));
    }

    clearAcademicScope();
    localStorage.setItem('token', impersonateToken);
    safeSetItem('user', userData);
    syncInstituteBranding(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${impersonateToken}`;

    try {
      const me = await axios.get(`${API}/auth/me`);
      let hydratedUser = me.data;
      if (hydratedUser?.custom_role_id) {
        try {
          const roleRes = await axios.get(`${API}/roles/${hydratedUser.custom_role_id}`);
          hydratedUser = { ...hydratedUser, custom_role: roleRes.data };
        } catch {}
      }
      safeSetItem('user', hydratedUser);
      syncInstituteBranding(hydratedUser);
      setUser(hydratedUser);
      return hydratedUser;
    } catch {
      setUser(userData);
      return userData;
    }
  };

  const stopImpersonation = async () => {
    const originalToken = sessionStorage.getItem('original_superadmin_token');
    const rawUser = sessionStorage.getItem('original_superadmin_user');

    sessionStorage.removeItem('original_superadmin_token');
    sessionStorage.removeItem('original_superadmin_user');
    setIsImpersonating(false);

    if (originalToken) {
      clearAcademicScope();
      localStorage.setItem('token', originalToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${originalToken}`;

      let parsedUser = null;
      try {
        if (rawUser) parsedUser = JSON.parse(rawUser);
      } catch {}

      resetThemeToDefaults();
      resetFaviconToDefault();
      resetPwaManifest();
      localStorage.removeItem('institute_branding');
      localStorage.removeItem('institute_category');
      window.dispatchEvent(new Event('institute-category-changed'));

      if (parsedUser) {
        safeSetItem('user', parsedUser);
        setUser(parsedUser);
      }

      try {
        const me = await axios.get(`${API}/auth/me`);
        safeSetItem('user', me.data);
        setUser(me.data);
        return me.data;
      } catch {
        return parsedUser;
      }
    }
    return null;
  };

  return (
    <AuthContext.Provider value={{ user, userRef, setUser, loading, login, completeTwoFactor, register, logout, updateUser, impersonateInstitute, stopImpersonation, isImpersonating }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export default AuthContext;
